// Grid rows (DESIGN.web.md 7.5): one row of a table is in the tab order at a
// time, the arrow keys move between rows, Home and End jump to the ends, and
// Enter or Space acts on the row in focus. Keys pressed on a control inside a
// row belong to that control.

import { useState, type KeyboardEvent } from 'react';

export interface GridRowProps {
  tabIndex: 0 | -1;
  'data-grid-row': '';
  onFocus: () => void;
  onKeyDown: (e: KeyboardEvent<HTMLTableRowElement>) => void;
}

/** Props for each record row, by id; rows without them (loading, notices) are skipped. */
export function useGridRows(ids: readonly string[]): (id: string, act: (row: HTMLTableRowElement) => void) => GridRowProps {
  const [active, setActive] = useState<string | null>(null);
  // The row that holds the tab stop: the last one focused while it is still
  // listed, else the first.
  const current = active !== null && ids.includes(active) ? active : (ids[0] ?? null);
  return (id, act) => ({
    tabIndex: id === current ? 0 : -1,
    'data-grid-row': '',
    onFocus: () => { if (id !== active) setActive(id); },
    onKeyDown: (e) => {
      if (e.target !== e.currentTarget) return;
      const rows = Array.from(e.currentTarget.parentElement?.querySelectorAll<HTMLTableRowElement>(':scope > tr[data-grid-row]') ?? []);
      const i = rows.indexOf(e.currentTarget);
      const go = (n: number) => { e.preventDefault(); rows[Math.max(0, Math.min(n, rows.length - 1))]?.focus(); };
      if (e.key === 'ArrowDown') go(i + 1);
      else if (e.key === 'ArrowUp') go(i - 1);
      else if (e.key === 'Home') go(0);
      else if (e.key === 'End') go(rows.length - 1);
      else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); act(e.currentTarget); }
    },
  });
}
